// BookReader.tsx
// Leitor de PDF com animação de folhear páginas
// Dependências: npm install pdfjs-dist
// O PDF.js worker deve ser configurado no seu vite.config / webpack conforme abaixo:
//   import { GlobalWorkerOptions } from 'pdfjs-dist';
//   GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

import React, { useEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../global-components/src";
import { useMessage } from "../../../global-components/src/index";
import { READBOOKS_API_URL } from "../../../global-components/src/utils/vars";
import "./style.css";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://unpkg.com/pdfjs-dist@5.6.205/build/pdf.worker.min.mjs";
  
// ─── Tipos ────────────────────────────────────────────────────────────────────

type FlipDirection = "forward" | "backward" | null;

interface PageCache {
  [pageNum: number]: HTMLCanvasElement;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const FLIP_DURATION = 600; // ms
const PROGRESS_DEBOUNCE = 2000; // ms entre saves de progresso

// ─── Componente principal ─────────────────────────────────────────────────────

export default function BookReader() {
  const { uuid } = useParams<{ uuid: string }>();
  const { token, user } = useAuth();
  const { setMessage } = useMessage();
  const navigate = useNavigate();

  // Estado do PDF
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estado da animação
  const [flipping, setFlipping] = useState(false);
  const [flipDir, setFlipDir] = useState<FlipDirection>(null);
  const [scale, setScale] = useState(1);

  // Canvas refs
  const currentCanvasRef = useRef<HTMLCanvasElement>(null);
  const nextCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cache de páginas renderizadas
  const pageCache = useRef<PageCache>({});
  const progressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const renderTask = useRef<pdfjsLib.RenderTask | null>(null);
  const leftCanvasRef = useRef<HTMLCanvasElement>(null);
  const magnifierRef = useRef<HTMLCanvasElement>(null);
  const [magnifier, setMagnifier] = useState<{
    visible: boolean;
    y: number;
    pageNum: number;
    side: "left" | "right";
    pageEl: HTMLElement;
    pageW: number;
    mouseYonPage: number;
    pageHeightPx: number;
  } | null>(null);
  const MAGNIFIER_HEIGHT = 220; // px — altura da faixa ampliada
  const MAGNIFIER_SCALE  = 3.5; // fator de zoom
  // ─── Remova todos os estados/refs da lupa ─────────────────────────────────────
  // Remova: magnifierRef, leftCanvasRef (se só era usado pela lupa), magnifier state,
  // MAGNIFIER_HEIGHT, MAGNIFIER_SCALE, MAGNIFIER_RENDER_SCALE,
  // drawMagnifier, onMouseMove, onMouseLeave

  // ─── Adicione estes estados e refs ───────────────────────────────────────────
  const [zoom, setZoom] = useState(2);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const pinchStartDist = useRef<number | null>(null);
  const pinchStartZoom = useRef(1);

  const MIN_ZOOM = 1;
  const MAX_ZOOM = 4;

  // ─── Clamp pan para não sair dos limites da página ────────────────────────────
  const clampPan = useCallback((x: number, y: number, z: number) => {
    if (!containerRef.current) return { x, y };
    const el = containerRef.current;
    const maxX = Math.max(0, (el.clientWidth  * (z - 1)) / 2);
    const maxY = Math.max(0, (el.clientHeight * (z - 1)) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  }, []);

  // ─── Ctrl+scroll ─────────────────────────────────────────────────────────────
  useEffect(() => {
    // O containerRef pode não estar pronto no primeiro render,
    // por isso buscamos o elemento direto no handler do documento
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      if (!containerRef.current?.contains(e.target as Node)) return;
      e.preventDefault();

      // Normaliza delta entre trackpad (pequeno) e scroll wheel (grande)
      const delta = e.deltaMode === 1
        ? e.deltaY * 30   // modo "linhas" (Firefox)
        : e.deltaMode === 2
          ? e.deltaY * 300 // modo "páginas"
          : e.deltaY;      // modo "pixels" (padrão)

      setZoom(prev => {
        const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev - delta * 0.003));
        setPan(p => clampPan(p.x, p.y, next));
        return next;
      });
    };

    // Escuta no document com capture=true para garantir que o preventDefault funcione
    // (listeners passivos no container filho não conseguem cancelar o zoom do browser)
    document.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => document.removeEventListener("wheel", onWheel, { capture: true });
  }, [clampPan]);


  // ─── Pan com mouse (quando zoom > 1) ─────────────────────────────────────────
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom <= 1) return;
    isPanning.current = true;
    panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  }, [zoom, pan]);

  const onMouseMovePan = useCallback((e: React.MouseEvent) => {
    if (!isPanning.current) return;
    const raw = {
      x: e.clientX - panStart.current.x,
      y: e.clientY - panStart.current.y,
    };
    setPan(clampPan(raw.x, raw.y, zoom));
  }, [zoom, clampPan]);

  const onMouseUpPan = useCallback(() => {
    isPanning.current = false;
  }, []);

  // ─── Pinch touch (mobile) ────────────────────────────────────────────────────
  const onTouchStartZoom = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStartDist.current = Math.hypot(dx, dy);
      pinchStartZoom.current = zoom;
    } else {
      // toque simples: swipe normal
      touchStartX.current = e.touches[0].clientX;
    }
  }, [zoom]);

  const onTouchMoveZoom = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchStartDist.current !== null) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM,
        pinchStartZoom.current * (dist / pinchStartDist.current)
      ));
      setZoom(next);
      setPan(p => clampPan(p.x, p.y, next));
    }
  }, [clampPan]);

  // ─── Reset zoom ao trocar página ─────────────────────────────────────────────
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [currentPage]);

  useEffect(() => {
    if (!uuid) return;

    const fetchReadUrl = async () => {
      try {
        const res = await axios.get(`${READBOOKS_API_URL}/catalog/${uuid}/read-url`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPdfUrl(res.data.url);

        // Recupera progresso salvo localmente
        const savedPage = localStorage.getItem(`reader-progress-${uuid}`);
        if (savedPage) setCurrentPage(Number(savedPage));
      } catch (err: any) {
        if (err.response?.status === 403) {
          setError(
            err.response.data?.needs_payment
              ? `Este livro custa R$ ${err.response.data.price}. Adquira-o para ler.`
              : "Você não tem acesso a este livro."
          );
        } else {
          setError("Não foi possível carregar o livro.");
        }
        setLoadingPdf(false);
      }
    };

    fetchReadUrl();
  }, [uuid, token]);

  

  // ─── Carrega o PDF ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!pdfUrl) return;

    const load = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({ url: pdfUrl, withCredentials: false });
        const doc = await loadingTask.promise;
        setPdfDoc(doc);
        setTotalPages(doc.numPages);
        setLoadingPdf(false);
      } catch(err) {
        console.error(err);
        setError("Erro ao carregar o PDF.");
        setLoadingPdf(false);
      }
    };

    load();
  }, [pdfUrl]);

  // ─── Calcula escala responsiva ────────────────────────────────────────────

  const calcScale = useCallback(async (doc: pdfjsLib.PDFDocumentProxy) => {
    if (!containerRef.current) return;
    const page = await doc.getPage(1);
    const viewport = page.getViewport({ scale: 1 });
    const containerW = containerRef.current.clientWidth / 2; // cada página ocupa metade
    const containerH = containerRef.current.clientHeight;
    const scaleW = containerW / viewport.width;
    const scaleH = containerH / viewport.height;
    setScale(Math.min(scaleW, scaleH, 1.5));
  }, []);

  useEffect(() => {
    if (pdfDoc) calcScale(pdfDoc);
  }, [pdfDoc, calcScale]);

  

  // ─── Renderiza uma página no canvas ──────────────────────────────────────

  const renderPage = useCallback(
    async (pageNum: number, canvas: HTMLCanvasElement) => {
      if (!pdfDoc || pageNum < 1 || pageNum > totalPages) return;

      // Usa cache se disponível
      if (pageCache.current[pageNum]) {
        const ctx = canvas.getContext("2d")!;
        canvas.width = pageCache.current[pageNum].width;
        canvas.height = pageCache.current[pageNum].height;
        ctx.drawImage(pageCache.current[pageNum], 0, 0);
        return;
      }

      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const ctx = canvas.getContext("2d")!;

      // Cancela render anterior se ainda estiver em andamento
      if (renderTask.current) {
        renderTask.current.cancel();
      }

      renderTask.current = page.render({ canvas: canvas, viewport });

      try {
        await renderTask.current.promise;

        // Salva no cache
        const offscreen = document.createElement("canvas");
        offscreen.width = canvas.width;
        offscreen.height = canvas.height;
        offscreen.getContext("2d")!.drawImage(canvas, 0, 0);
        pageCache.current[pageNum] = offscreen;
      } catch (e: any) {
        if (e?.name !== "RenderingCancelledException") console.error(e);
      }
    },
    [pdfDoc, scale, totalPages]
  );

  const drawMagnifier = useCallback(
      (pageNum: number, mouseYonPage: number, pageHeightPx: number, pageEl: HTMLElement) => {
        if (!magnifierRef.current || !pdfDoc) return;
        const sourceCanvas = pageCache.current[pageNum];
        if (!sourceCanvas) return;

        const mCanvas = magnifierRef.current;
        const ctx = mCanvas.getContext("2d")!;

        // largura da lupa = largura exata do elemento da página no DOM
        const pageW = pageEl.offsetWidth;
        const pageH = pageEl.offsetHeight;

        // Fatia no canvas de origem (mantém proporção 1:1 com o canvas real)
        // Quantos pixels do sourceCanvas correspondem a MAGNIFIER_HEIGHT na tela?
        const scaleRatio = sourceCanvas.height / pageH; // px canvas por px DOM
        const sliceH_src = (MAGNIFIER_HEIGHT / MAGNIFIER_SCALE) * scaleRatio;
        const sliceW_src = sourceCanvas.width; // largura total da página, sem crop horizontal

        // Centro vertical da fatia
        const centerY_src = (mouseYonPage / pageH) * sourceCanvas.height;
        const sliceY_src = Math.max(
          0,
          Math.min(centerY_src - sliceH_src / 2, sourceCanvas.height - sliceH_src)
        );

        // Canvas de saída: largura = página, altura = faixa
        mCanvas.style.width  = `${pageW}px`;
        mCanvas.style.height = `${MAGNIFIER_HEIGHT}px`;

        // Limpa
        ctx.clearRect(0, 0, pageW, MAGNIFIER_HEIGHT);

        // Desenha mantendo proporção correta (sem distorção)
        ctx.drawImage(
          sourceCanvas,
          0,          sliceY_src,
          sliceW_src, sliceH_src,
          0,          0,
          pageW,      MAGNIFIER_HEIGHT
        );
      },
      [pdfDoc]
    );

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (flipping) return;

      const containerRect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      const containerW = containerRect.width;

      // Descobre em qual metade o mouse está
      const isRightSide = mouseX > containerW / 2;
      const pageNum     = isRightSide ? currentPage : currentPage - 1;
      if (pageNum < 1 || pageNum > totalPages) { setMagnifier(null); return; }

      // Busca o elemento DOM da página correta
      const bookEl  = containerRef.current?.querySelector(".reader-book") as HTMLElement;
      const pageEls = bookEl?.querySelectorAll(".reader-page") as NodeListOf<HTMLElement>;
      // pageEls[0] = esquerda, pageEls[1] = direita
      const pageEl  = isRightSide ? pageEls?.[1] : pageEls?.[0];
      if (!pageEl) return;

      // Y do mouse relativo ao topo da página
      const pageRect    = pageEl.getBoundingClientRect();
      const mouseYonPage = e.clientY - pageRect.top;
      if (mouseYonPage < 0 || mouseYonPage > pageRect.height) { setMagnifier(null); return; }

      // Pré-renderiza no cache se necessário
      if (!pageCache.current[pageNum] && pdfDoc) {
        const offscreen = document.createElement("canvas");
        renderPage(pageNum, offscreen);
      }

      // Posição Y da lupa: acima do cursor se perto do fundo
      const lensH = MAGNIFIER_HEIGHT;
      const spaceBelow = pageRect.bottom - e.clientY;
      const posY = spaceBelow < lensH + 20
        ? mouseYonPage - lensH + 70
        : mouseYonPage - 120;

      // Alinha a lupa horizontalmente ao topo da página no container
      const pageTopInContainer = pageRect.top - containerRect.top;

      setMagnifier({
        visible:   true,
        y:         pageTopInContainer + posY,
        pageNum,
        side:      isRightSide ? "right" : "left",
        pageEl,
        pageW:     pageRect.width + (isRightSide ? -20 : 25),
        mouseYonPage,
        pageHeightPx: pageRect.height,
      });

      drawMagnifier(pageNum, mouseYonPage, pageRect.height, pageEl);
    },
    [currentPage, totalPages, flipping, pdfDoc, renderPage, drawMagnifier]
  );
  const onMouseLeave = useCallback(() => {
    setMagnifier(null);
  }, []);

  // ─── Renderiza página atual quando tudo estiver pronto ────────────────────

  useEffect(() => {
    if (!pdfDoc || !currentCanvasRef.current) return;
    setLoadingPage(true);
    renderPage(currentPage, currentCanvasRef.current).then(() => setLoadingPage(false));
    
    // ← ADICIONE: renderiza a página anterior no lado esquerdo
    if (currentPage > 1 && leftCanvasRef.current) {
      renderPage(currentPage - 1, leftCanvasRef.current);
    }

    // Pré-carrega próxima página no cache
    if (currentPage + 1 <= totalPages) {
      const offscreen = document.createElement("canvas");
      renderPage(currentPage + 1, offscreen);
    }
  }, [pdfDoc, currentPage, renderPage, totalPages]);

  // ─── Animação de virar página ─────────────────────────────────────────────

  const flipPage = useCallback(async (direction: "forward" | "backward") => {
    if (flipping || !pdfDoc) return;
    const targetPage = direction === "forward" ? currentPage + 1 : currentPage - 1;
    if (targetPage < 1 || targetPage > totalPages) return;

    if (nextCanvasRef.current) {
      // forward: overlay mostra página atual saindo
      // backward: overlay mostra página esquerda saindo
      const pageToShow = direction === "forward" ? currentPage : currentPage - 1;
      await renderPage(pageToShow, nextCanvasRef.current);
    }

    setFlipDir(direction);
    setFlipping(true);
    setTimeout(() => {
      setCurrentPage(targetPage);
      setFlipping(false);
      setFlipDir(null);
      saveProgress(targetPage);
    }, FLIP_DURATION);
  }, [flipping, pdfDoc, currentPage, totalPages, renderPage]);

  

  const onTouchEndZoom = useCallback((e: React.TouchEvent) => {
    if (pinchStartDist.current !== null) {
      pinchStartDist.current = null;
      return;
    }
    // swipe normal
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) flipPage(diff > 0 ? "forward" : "backward");
    touchStartX.current = null;
  }, [flipPage]);
  // ─── Navegação por teclado ────────────────────────────────────────────────

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") flipPage("forward");
      if (e.key === "ArrowLeft") flipPage("backward");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flipPage]);

  // ─── Swipe touch ──────────────────────────────────────────────────────────

  const touchStartX = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      flipPage(diff > 0 ? "forward" : "backward");
    }
    touchStartX.current = null;
  };

  // ─── Salva progresso no servidor (debounced) ──────────────────────────────

  const saveProgress = (page: number) => {
    localStorage.setItem(`reader-progress-${uuid}`, String(page));

    if (progressTimer.current) clearTimeout(progressTimer.current);
    progressTimer.current = setTimeout(async () => {
      try {
        await axios.post(
          `${READBOOKS_API_URL}/catalog/${uuid}/progress`,
          { page },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch {
        // silencioso — progresso já está salvo localmente
      }
    }, PROGRESS_DEBOUNCE);
  };

  // ─── Ir para página específica ────────────────────────────────────────────

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages || flipping) return;
    setCurrentPage(page);
    saveProgress(page);
  };

  // ─── Estados de UI ────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="reader-error">
        <div className="reader-error-icon">📖</div>
        <p>{error}</p>
        <button onClick={() => navigate(-1)}>Voltar</button>
      </div>
    );
  }

  if (loadingPdf) {
    return (
      <div className="reader-loading">
        <div className="reader-loading-book">
          <div className="reader-loading-page" />
          <div className="reader-loading-page" />
        </div>
        <p>Carregando livro...</p>
      </div>
    );
  }

  const progress = totalPages ? Math.round((currentPage / totalPages) * 100) : 0;

  return (
    <div className="reader-root">
      {/* ── Cabeçalho ── */}
      <div className="reader-header">
        <button className="reader-btn-back" onClick={() => navigate(-1)}>
          ← Voltar
        </button>
        <div className="reader-page-info">
          Página{" "}
          <input
            className="reader-page-input"
            type="number"
            min={1}
            max={totalPages}
            value={currentPage}
            onChange={(e) => goToPage(Number(e.target.value))}
          />{" "}
          de {totalPages}
        </div>
        <div className="reader-progress-bar">
          <div className="reader-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        {zoom > 1 && (
          <button
            className="reader-btn-zoom-reset"
            onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
          >
            {Math.round(zoom * 100)}% · Resetar
          </button>
        )}
      </div>

      {/* ── Livro ── */}
      <div
        className="reader-book-container"
        ref={containerRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMovePan}
        onMouseUp={onMouseUpPan}
        onMouseLeave={onMouseUpPan}
        onTouchStart={onTouchStartZoom}
        onTouchMove={onTouchMoveZoom}
        onTouchEnd={onTouchEndZoom}
        style={{ cursor: zoom > 1 ? (isPanning.current ? "grabbing" : "grab") : "default" }}
      >
        <div className="reader-book-shadow" />

        {/* Wrapper que aplica zoom + pan */}
        <div
          className="reader-zoom-wrapper"
          style={{
            transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
            transformOrigin: "center center",
            transition: isPanning.current ? "none" : "transform 0.15s ease",
          }}
        >
          <div className={`reader-book ${flipping ? `flipping-${flipDir}` : ""}`}>
            {/* página esquerda */}
            <div className="reader-page reader-page-left">
              {currentPage > 1
                ? <canvas ref={leftCanvasRef} className="reader-canvas" />
                : <div className="reader-page-blank" />}
            </div>

            {/* página direita */}
            <div className="reader-page reader-page-right">
              {loadingPage && <div className="reader-page-spinner" />}
              <canvas ref={currentCanvasRef} className="reader-canvas" />
            </div>

            {/* overlay de flip */}
            {flipping && (
              <div className={`reader-flip-overlay reader-flip-${flipDir}`}>
                <div className="reader-flip-front">
                  <canvas ref={nextCanvasRef} className="reader-canvas" />
                </div>
                <div className="reader-flip-back" />
              </div>
            )}
          </div>
        </div>

        {/* botões de clique ficam fora do wrapper para não escalar junto */}
        <button
          className="reader-click-area reader-click-left"
          onClick={() => zoom === 1 && flipPage("backward")}
          aria-label="Página anterior"
          disabled={currentPage <= 1 || flipping || zoom > 1}
        />
        <button
          className="reader-click-area reader-click-right"
          onClick={() => zoom === 1 && flipPage("forward")}
          aria-label="Próxima página"
          disabled={currentPage >= totalPages || flipping || zoom > 1}
        />
      </div>

      {/* ── Rodapé com navegação ── */}
      <div className="reader-footer">
        <button
          className="reader-nav-btn"
          onClick={() => flipPage("backward")}
          disabled={currentPage <= 1 || flipping}
        >
          ‹ Anterior
        </button>

        <div className="reader-progress-text">{progress}% lido</div>

        <button
          className="reader-nav-btn reader-nav-btn-primary"
          onClick={() => flipPage("forward")}
          disabled={currentPage >= totalPages || flipping}
        >
          Próxima ›
        </button>
      </div>
    </div>
  );
}